import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { MapEngineGate } from '../map-engine/map-engine-gate.service';
import { MAP_REFRESH } from '../map-engine/map-refresh.token';
import { AppContext, CommandRegistryService, Command } from './command-registry.service';
import { DelegationService } from './delegation.service';
import { BookingService } from './booking.service';
import { SelectionStateService } from './selection-state.service';
import { UrlStateService } from './url-state.service';
import { MapActionBusService } from './map-action-bus.service';
import { DelegatePickerService } from '../../shared/components/delegate-picker-dialog/delegate-picker.service';
import { QuickBookService } from '../../shared/components/quick-book-dialog/quick-book.service';
import { FeatureFlagsViewerService } from './feature-flags-viewer.service';
import { FeatureFlagsService } from './feature-flags.service';
import { WORK_STATUS_TO_EN } from '../models/person.model';
import { localizeStatus } from '../constants/status-labels';

/**
 * RESPONSIBILITY: Registers the palette commands that work from any page: delegation, personal
 * booking, directions, and utility actions (find person, quick book, report issue). The commands
 * that exist only on the map (view modes, lighting, themes, floor and building switching, the
 * walk, the time trial and the arcade) are `MapPageCommandsService`, in the engine chunk, so this
 * service carries no engine code and can sit in the entry bundle.
 *
 * SIGNALS: None — this service produces commands, not signals. Commands are
 *   registered with CommandRegistryService and consumed by the command palette.
 *
 * USED BY: Instantiated at app root level (providedIn: 'root'). Consumed
 *   indirectly by CommandPaletteComponent via CommandRegistryService.
 *
 * BACKEND CONTRACT: None directly — delegates to BookingService, DelegationService,
 *   MapActionBusService, and (through MapEngineGate) the engine's orchestrator and routing.
 *
 * AI CONTEXT: Commands are categorised as primary (appear in empty-query list)
 *   or secondary (primary: false, only appear when user searches).
 *   Aliases include translations (DE, FR, NL, JA) for multilingual search.
 */
@Injectable({ providedIn: 'root' })
export class MapCommandsService {
  constructor() {
    const registry = inject(CommandRegistryService);
    const delegation = inject(DelegationService);
    const delegatePicker = inject(DelegatePickerService);
    const booking = inject(BookingService);
    const selection = inject(SelectionStateService);
    const urlState = inject(UrlStateService);
    const actionBus = inject(MapActionBusService);
    const quickBook = inject(QuickBookService);
    const router = inject(Router);
    // The engine (orchestrator, routing) is a lazy chunk; these commands run after a click, so they load it first.
    const engine = inject(MapEngineGate);
    // After a booking change the floor's availability is reloaded through this: it never waits for the engine download.
    const mapRefresh = inject(MAP_REFRESH);
    const transloco = inject(TranslocoService);
    const featureFlags = inject(FeatureFlagsViewerService);
    const trialFlags = inject(FeatureFlagsService);

    /** Boost score when on the map page (global commands — visible everywhere) */
    const mapBoost = (ctx: AppContext) => ctx.currentSection === 'map' ? 25 : 0;
    /** Only visible when on the map page (map-specific commands) */
    const mapOnly = (ctx: AppContext) => ctx.currentSection === 'map';

    // ═══════════════════════════════════════════════════════════
    // PRIMARY actions — visible in the default (empty query) list
    //
    // Global: visible on all pages (Quick Book, Find Person, Check In, etc.)
    // Map-only: visible only on /map (view modes, lighting, floor switch, etc.)
    // ═══════════════════════════════════════════════════════════

    // ── Delegation ─────────────────────────────────────────────

    registry.register({
      id: 'action:book-on-behalf',
      title: 'Book on behalf of...',
      subtitle: 'Book a desk or room for someone else',
      aliases: ['delegate', 'behalf', 'on behalf', 'proxy booking', 'book for', 'book for someone', 'someone else',
        'stellvertretend buchen', 'im auftrag', 'réserver pour', 'au nom de', 'namens boeken', '代理予約'],
      icon: 'person-swap',
      group: 'actions',
      score: 70,
      visible: (ctx) =>
        ctx.userActions.includes('booking:create:on-behalf') &&
        !delegation.state().active,
      contextBoost: mapBoost,
      execute: async () => {
        const employee = await delegatePicker.pick();
        if (employee) delegation.activateFor(employee);
      },
    });

    registry.register({
      id: 'action:exit-delegation',
      title: 'Exit delegation mode',
      subtitle: 'Stop booking on behalf of others',
      aliases: ['stop behalf', 'exit behalf', 'cancel delegation', 'deactivate delegation', 'end delegation',
        'delegation beenden', 'arrêter délégation', 'delegatie stoppen', '代理終了'],
      icon: 'close',
      group: 'actions',
      score: 90,
      visible: () => delegation.state().active,
      execute: () => delegation.deactivate(),
    });

    // ── Personal Booking ───────────────────────────────────────

    registry.register({
      id: 'action:go-to-my-desk',
      title: 'Go to my desk',
      subtitle: 'Fly to your booked desk on the map',
      aliases: ['my desk', 'my booking', 'where am i', 'find my desk', 'my space', 'show my desk', 'my seat', 'my place',
        'mein schreibtisch', 'wo bin ich', 'mon bureau', 'où suis-je', 'mijn bureau', '自分のデスク'],
      icon: 'desk',
      group: 'actions',
      score: 75,
      visible: () => booking.myBookings().some(b => b.bookingType === 'desk'),
      contextBoost: mapBoost,
      execute: () => {
        const deskBooking = booking.myBookings().find(b => b.bookingType === 'desk');
        if (deskBooking?.spaceId) {
          router.navigate(['/map']);
          actionBus.dispatch({ type: 'select-space', spaceId: deskBooking.spaceId });
        }
      },
    });

    registry.register({
      id: 'action:directions',
      title: 'Directions',
      subtitle: 'Walking directions on the map — from anywhere, to anywhere',
      aliases: ['directions', 'route', 'how do i get to', 'find my way', 'wayfinding', 'navigate', 'walk to',
        'wegbeschreibung', 'itinéraire', 'indicaciones', 'indicazioni', 'routebeschrijving', '経路案内'],
      icon: 'directions',
      group: 'actions',
      score: 72,
      contextBoost: mapBoost,
      execute: () => {
        router.navigate(['/map']);
        selection.requestDirections(null);
      },
    });

    registry.register({
      id: 'action:directions-to-my-desk',
      title: 'Directions to my desk',
      subtitle: 'Walk to the desk you have booked',
      aliases: ['route to my desk', 'walk to my desk', 'how do i get to my desk', 'directions desk',
        'weg zu meinem arbeitsplatz', 'itinéraire vers mon bureau', 'route naar mijn werkplek', '自分のデスクへの経路'],
      icon: 'directions',
      group: 'actions',
      score: 74,
      visible: () => booking.myBookings().some(b => b.bookingType === 'desk'),
      contextBoost: mapBoost,
      execute: async () => {
        const desk = booking.myBookings().find(b => b.bookingType === 'desk');
        if (!desk?.spaceId) return;
        const spaceId = desk.spaceId;
        router.navigate(['/map']);
        // Failing to load the engine must not swallow the directions panel: the route is then simply not drawn.
        try { await engine.use(e => e.routing.setRouteTo(spaceId, undefined, { source: 'palette' })); } catch (err) { console.warn('[MapCommands] route not set:', err); }
        selection.requestDirections('to');
      },
    });

    registry.register({
      id: 'action:check-in',
      title: 'Check in',
      subtitle: 'Check in to your current booking',
      aliases: ['check in', 'checkin', 'arrive', 'im here', 'arrived', 'i am here', 'sign in',
        'einchecken', 'ich bin da', 'enregistrer', 'je suis là', 'inchecken', 'チェックイン'],
      icon: 'check-circle',
      group: 'actions',
      score: 75,
      visible: () => booking.myBookings().length > 0,
      contextBoost: mapBoost,
      execute: async () => {
        const current = booking.myBookings()[0];
        if (current) {
          const success = await booking.checkInBooking(current._id, 'command-palette');
          if (success) mapRefresh.refreshAvailabilityAndColors().catch(() => {});
        }
      },
    });

    registry.register({
      id: 'action:cancel-my-booking',
      title: 'Cancel my booking',
      subtitle: 'Cancel your desk booking for today',
      aliases: ['cancel booking', 'cancel desk', 'release desk', 'unbook', 'remove booking', 'delete booking',
        'buchung stornieren', 'annuler réservation', 'boeking annuleren', '予約キャンセル'],
      icon: 'close',
      group: 'actions',
      score: 60,
      visible: () => booking.myBookings().some(b => b.bookingType === 'desk'),
      contextBoost: mapBoost,
      execute: async () => {
        const deskBooking = booking.myBookings().find(b => b.bookingType === 'desk');
        if (deskBooking) {
          const success = await booking.cancelBooking(deskBooking._id);
          if (success) mapRefresh.refreshAvailabilityAndColors().catch(() => {});
        }
      },
    });

    // ── Utility — Primary ──────────────────────────────────────

    registry.register({
      id: 'action:find-a-time',
      title: 'Find a time',
      subtitle: 'Smart meeting room scheduling assistant',
      aliases: ['find a time', 'schedule meeting', 'book meeting', 'meeting room', 'schedule room', 'find room', 'book a room', 'reserve room',
        'zeit finden', 'besprechung planen', 'trouver un créneau', 'planifier réunion', 'tijd vinden', 'vergadering plannen', '会議室予約'],
      icon: 'sparkles',
      group: 'actions',
      score: 65,
      contextBoost: mapBoost,
      execute: () => {
        router.navigate(['/map']);
        setTimeout(() => actionBus.dispatch({ type: 'open-scheduling' }), 150);
      },
    });

    registry.register({
      id: 'action:office-info',
      title: 'Show office info',
      subtitle: 'Building details, occupancy, and team status',
      aliases: ['office info', 'building info', 'occupancy', 'who is in', 'whos in', 'office details', 'building details', 'office hours',
        'büroinformationen', 'gebäudeinfo', 'informations bureau', 'kantoorinfo', 'オフィス情報'],
      icon: 'building',
      group: 'actions',
      score: 55,
      visible: mapOnly,
      execute: () => {
        router.navigate(['/map']);
        urlState.setOfficeInfoOpen(true);
      },
    });

    registry.register({
      id: 'action:report-issue',
      title: 'Report an issue',
      subtitle: 'Report a problem with a space or facility',
      aliases: ['report issue', 'raise issue', 'problem', 'broken', 'maintenance', 'fix', 'something wrong', 'not working', 'damaged',
        'problem melden', 'störung', 'signaler problème', 'probleem melden', 'kapot', '問題報告'],
      icon: 'alert-triangle',
      group: 'actions',
      score: 50,
      // Capability-gated (`issues.submission`) on top of the map-only
      // context — `is()` fails closed until the viewer flag set loads,
      // matching the gated issue surfaces this command launches.
      visible: (ctx) => mapOnly(ctx) && featureFlags.is('issues.submission'),
      execute: () => {
        router.navigate(['/map']);
        setTimeout(() => actionBus.dispatch({ type: 'open-issue-report', context: { entry: 'command-palette' } }), 150);
      },
    });

    registry.register({
      id: 'action:find-person',
      title: 'Find a person',
      subtitle: 'Search for someone and see where they are',
      aliases: ['find person', 'find colleague', 'where is', 'locate', 'who', 'find employee', 'find someone', 'search person', 'look for',
        'person finden', 'wo ist', 'kollege finden', 'trouver personne', 'où est', 'persoon zoeken', 'waar is', '人を探す'],
      icon: 'search',
      group: 'actions',
      score: 65,
      contextBoost: mapBoost,
      execute: async () => {
        const person = await delegatePicker.pick({ mode: 'find', title: 'Find a person' });
        if (!person) return;

        if (person.currentSpaceId) {
          // Person has a desk booking — fly to them on the map
          router.navigate(['/map']);
          actionBus.dispatch({ type: 'select-space', spaceId: person.currentSpaceId });
        } else if (person.status && person.status !== 'none' && person.status !== 'office') {
          // Person not in office — show toast with their status (localised)
          const label = localizeStatus(WORK_STATUS_TO_EN[person.status], transloco);
          actionBus.dispatch({
            type: 'show-toast',
            message: `${person.name} — ${label.toLowerCase()}`,
          });
        } else {
          actionBus.dispatch({
            type: 'show-toast',
            message: `${person.name} doesn't have a desk booked today`,
          });
        }
      },
    });

    registry.register({
      id: 'action:quick-book',
      title: 'Smart book',
      subtitle: 'Plan your week — desks, parking and where you\'ll be',
      aliases: ['smart book', 'quick book', 'plan my week', 'plan week', 'book desk', 'book parking', 'find desk', 'grab desk', 'reserve desk', 'need a desk', 'get a desk', 'book a space',
        'schnellbuchung', 'schreibtisch buchen', 'réservation rapide', 'réserver bureau', 'snel boeken', 'bureau boeken', 'クイック予約'],
      icon: 'zap',
      group: 'actions',
      score: 70,
      contextBoost: mapBoost,
      execute: async () => {
        const booked = await quickBook.start({ source: 'palette' });
        if (booked) mapRefresh.refreshAvailabilityAndColors().catch(() => {});
      },
    });

    // Plan my week (Nath, 4 Oct: "I can't currently see this"). With home-workspace-week on it goes to My week,
    // Louis's planner page at /home/my-week (5 Oct); otherwise it opens Smart Book, as ⌘B does.
    registry.register({
      id: 'action:plan-week',
      title: 'Plan my week',
      subtitle: 'Where you\'ll be each day, with desks and parking, a week at a time',
      aliases: ['plan my week', 'plan week', 'plan next week', 'week planner', 'smart fill', 'my week',
        'woche planen', 'planifier ma semaine', 'week plannen', '週の予定'],
      icon: 'calendar',
      group: 'actions',
      score: 72,
      execute: () => {
        if (trialFlags.flagEnabled('home-workspace-week')) void router.navigate(['/home/my-week']);
        else void quickBook.start({ source: 'palette' });
      },
    });

  }
}
